import { DescriptionList } from "@/components/display/DescriptionList";
import type { EmbedPage } from "@/features/supabase";
import { useGetDatasetDashboardInfoQuery } from "@/store/services/metabase";
import { formatGeographicResolution } from "@/utils/admin/geographicResolution";
import { formatDateOnly } from "@/utils/datetime";
import { Skeleton, Tag, Typography } from "antd";
import { getDatasetMetricLabels } from "../functions";
// import { DatasetAvailabilityTag } from "./DatasetAvailabilityTag";

const NotSet = () => <span className="text-gray-400" data-sentry-component="NotSet" data-sentry-source-file="DatasetSidebar.tsx">Not set</span>;

/** Everything we know about the selected dataset, beside the dashboard itself. */
export const DatasetSidebar = ({
  dataset
}: {
  dataset: EmbedPage;
}) => {
  // "Last updated" lives in Metabase, not in embed_pages, so it needs its own fetch.
  const {
    data: dashboardInfo,
    isLoading
  } = useGetDatasetDashboardInfoQuery(dataset.id, {
    skip: dataset.embed_type !== "metabase-guest-embed"
  });
  const metrics = getDatasetMetricLabels(dataset.metrics);
  const items = [{
    key: "updated",
    label: "Last updated",
    value: isLoading ? <Skeleton.Input active size="small" style={{
      width: 110,
      height: 18
    }} /> : dashboardInfo?.updatedAt ? formatDateOnly(dashboardInfo.updatedAt) : <NotSet />
  }, {
    key: "coverage",
    label: "Coverage",
    value: formatGeographicResolution(dataset.geographic_resolution)
  }, {
    key: "metrics",
    label: "Metrics",
    value: metrics.length ? <span className="flex flex-wrap gap-1">
          {metrics.map(metric => <Tag key={metric} className="m-0">
              {metric}
            </Tag>)}
        </span> : <NotSet />
  }];

  // The dataset's own description is the curated one; fall back to Metabase's.
  const description = dataset.description?.trim() || dashboardInfo?.description?.trim() || "";
  return <aside className="flex h-full w-[320px] shrink-0 flex-col overflow-y-auto border-r border-gray-200 bg-surface" data-sentry-component="DatasetSidebar" data-sentry-source-file="DatasetSidebar.tsx">
      <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-3">
        <Typography.Title className="!m-0 !text-base !font-semibold" data-sentry-element="unknown" data-sentry-source-file="DatasetSidebar.tsx">{dataset.title}</Typography.Title>
        {dataset.subtitle && <span className="text-sm text-gray-500">{dataset.subtitle}</span>}
        {/*<DatasetAvailabilityTag canOpen={dataset.can_open} />*/}
      </div>

      <div className="flex flex-col gap-4 px-4 py-4">
        {description && <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-gray-800">About</span>
            <p className="m-0 whitespace-pre-line text-sm text-gray-500">{description}</p>
          </div>}

        <DescriptionList title="Details" items={items} labelWidth={110} data-sentry-element="DescriptionList" data-sentry-source-file="DatasetSidebar.tsx" />
      </div>
    </aside>;
};